import { useEffect, useRef, useState } from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import clsx from 'clsx';

import { mediaUrl } from '@/lib/media';

// Одновременно играет одно голосовое: включили следующее — прошлое на паузе.
let active: HTMLAudioElement | null = null;

const SPEEDS = [1, 1.5, 2] as const;
type Speed = (typeof SPEEDS)[number];

export function formatClock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Голосовое без волны (запись без анализатора) — ровная «пила», чтобы было за что тянуть.
const FLAT = Array.from({ length: 36 }, (_, i) => 22 + ((i * 37) % 34));

export default function VoicePlayer({
  url,
  durationMs,
  waveform,
  own,
}: {
  url: string;
  durationMs: number;
  waveform: number[];
  own: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingSeek = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState<Speed>(1);
  const [failed, setFailed] = useState(false);
  const bars = waveform.length ? waveform : FLAT;

  useEffect(
    () => () => {
      const a = audioRef.current;
      if (!a) return;
      a.pause();
      a.removeAttribute('src');
      if (active === a) active = null;
    },
    [],
  );

  const durationSec = (a: HTMLAudioElement) =>
    Number.isFinite(a.duration) && a.duration > 0 ? a.duration : durationMs / 1000;

  const ensureAudio = (): HTMLAudioElement => {
    if (audioRef.current) return audioRef.current;
    const a = new Audio();
    a.preload = 'auto';
    a.addEventListener('timeupdate', () => {
      const d = durationSec(a);
      if (d > 0) setProgress(Math.min(1, a.currentTime / d));
    });
    a.addEventListener('playing', () => {
      setLoading(false);
      setPlaying(true);
    });
    a.addEventListener('pause', () => setPlaying(false));
    a.addEventListener('waiting', () => setLoading(true));
    a.addEventListener('ended', () => {
      setPlaying(false);
      setProgress(0);
    });
    a.addEventListener('error', () => {
      setLoading(false);
      setPlaying(false);
      setFailed(true);
    });
    a.addEventListener('loadedmetadata', () => {
      if (pendingSeek.current == null) return;
      a.currentTime = pendingSeek.current * durationSec(a);
      pendingSeek.current = null;
    });
    a.src = mediaUrl(url) ?? '';
    audioRef.current = a;
    return a;
  };

  const toggle = () => {
    const a = ensureAudio();
    if (!a.paused) {
      a.pause();
      return;
    }
    if (active && active !== a) active.pause();
    active = a;
    setFailed(false);
    setLoading(true);
    a.playbackRate = speed;
    if (a.ended) a.currentTime = 0;
    void a.play().catch(() => {
      setLoading(false);
      setFailed(true);
    });
  };

  const seek = (ratio: number) => {
    const a = ensureAudio();
    setProgress(ratio);
    if (a.readyState >= 1) a.currentTime = ratio * durationSec(a);
    else pendingSeek.current = ratio;
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]!;
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const played = Math.round(progress * bars.length);
  const shownMs = playing || progress > 0 ? progress * durationMs : durationMs;

  return (
    <div className="flex w-[232px] max-w-full items-center gap-2.5">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Пауза' : 'Слушать голосовое'}
        className={clsx(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition active:scale-95',
          own ? 'bg-white text-orange' : 'bg-orange text-white',
        )}
      >
        {loading ? (
          <Loader2 size={18} className="animate-spin" aria-hidden />
        ) : playing ? (
          <Pause size={17} fill="currentColor" aria-hidden />
        ) : (
          <Play size={17} fill="currentColor" className="ml-0.5" aria-hidden />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div
          role="slider"
          tabIndex={0}
          aria-label="Перемотка голосового"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          className="flex h-7 cursor-pointer items-center gap-[2px]"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            seek(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)));
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') seek(Math.min(1, progress + 0.05));
            if (e.key === 'ArrowLeft') seek(Math.max(0, progress - 0.05));
          }}
        >
          {bars.map((v, i) => (
            <span
              key={i}
              className={clsx(
                'min-w-0 flex-1 rounded-full',
                i < played ? (own ? 'bg-white' : 'bg-orange') : own ? 'bg-white/45' : 'bg-ink/20',
              )}
              style={{ height: `${Math.max(14, v)}%` }}
            />
          ))}
        </div>
        <div className={clsx('mt-0.5 flex items-center justify-between text-[11px] tabular-nums', own ? 'text-white/85' : 'text-ink/50')}>
          <span>{failed ? 'не воспроизводится' : formatClock(shownMs)}</span>
          {playing || speed !== 1 ? (
            <button
              type="button"
              onClick={cycleSpeed}
              className={clsx('rounded-full px-1.5 font-semibold', own ? 'bg-white/20' : 'bg-ink/5')}
              aria-label="Скорость воспроизведения"
            >
              {speed}×
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
